import React from "react";
import { TEmailCompositionForm } from "../../../../../../domain/codecs/form/EmailCompositionForm";
import { CRMColors } from "../../../../models/CRMColors";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { Padding } from "../../../BuildingBlocks/Padding";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { CRMButtonFlatColourIcon } from "../../../CRMButtonFlatColourIcon/CRMButtonFlatColourIcon";
import { isEmailViewType, TEmailViewStatus } from "../CRMComposeEmailForm/CRMComposeEmailFormProps";
import { Background } from "../../../BuildingBlocks/Background";
import { TDictationStatus } from "../CRMComposeEmailForm/useDictation";

export type TEmailCloseMode = "clear-only" | "close-and-clear";

const formatElapsed = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

type TComposeEmailControlsProps = {
    viewStatus: TEmailViewStatus;
    form: TEmailCompositionForm;
    closeMode: TEmailCloseMode;
    isACaseSelected: boolean;
    isSubmittable: boolean;

    aiReviewStatus: "idle" | "loading" | "success" | "error";
    onClickAiReview: () => void;

    dictationStatus: TDictationStatus;
    dictationElapsedSeconds: number;
    onClickDictate: () => void;
    onStopDictate: () => void;

    isSendInProgress: boolean;
    onSend: () => void;
    onClose?: () => void;
    onClear?: () => void;
    onClickAttachment: () => void;
    onClickEmailTemplates: () => void;
};

export const CRMComposeEmailControls = (props: React.PropsWithChildren<TComposeEmailControlsProps>): JSX.Element => {

    return (
        <Background
            padding={`${CRMSpacing.TINY} ${CRMSpacing.MEDIUM}`}
            width="100%"
        >
            <SpacingRow
                spacing={CRMSpacing.SMALL}
                alignItems="center"
                childSize={"1fr"}
                justifyContent="space-between"
            >
                {/* COMPOSE EMAIL CONTROLS */}
                <SpacingRow justifyContent="space-between">

                    {/* CLOSE POPUP */}
                    <SpacingRow spacing={CRMSpacing.SMALL}>
                        {props.closeMode === "close-and-clear" &&
                            <CRMButtonFlatColourIcon
                                backgroundColor={CRMColors.NEUTRAL_PAPER}
                                icon={"arrow-left"}
                                iconColor={CRMColors.NEUTRAL_INK}
                                onClick={props.onClose}
                                disabled={props.form.status === "submitting"}
                                title="Back"
                            />
                        }
                        
                        <CRMButtonFlatColourIcon
                            backgroundColor={CRMColors.NEUTRAL_PAPER}
                            icon={"delete-all"}
                            iconColor={CRMColors.NEUTRAL_INK}
                            onClick={props.onClear}
                            disabled={props.form.status === "submitting"}
                            title="Clear All"
                        />
                    </SpacingRow>

                    <SpacingRow spacing={CRMSpacing.SMALL}>
                        {/* USE TEMPLATE */}
                        <CRMButtonFlatColourIcon
                            backgroundColor={CRMColors.PRIMARY_12}
                            iconColor={CRMColors.NEUTRAL_INK}
                            icon="chat-points"
                            onClick={props.onClickEmailTemplates}
                            title="Choose email template"
                        />

                        {/* AI CLEAR REVIEW */}
                        <CRMButtonFlatColourIcon
                            backgroundColor={props.aiReviewStatus === "loading" ? CRMColors.NEUTRAL_6 : CRMColors.HIGHLIGHTS_CALMING_PINK_10}
                            iconColor={props.aiReviewStatus === "loading" ? CRMColors.NEUTRAL_4 : CRMColors.NEUTRAL_INK}
                            icon="lightbulb"
                            onClick={props.aiReviewStatus === "loading" ? undefined : props.onClickAiReview}
                            title="AI review (CLEAR framework)"
                        />

                        {/* DICTATE */}
                        <CRMButtonFlatColourIcon
                            backgroundColor={props.dictationStatus === "recording" ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0 : CRMColors.HIGHLIGHTS_CALMING_PINK_10}
                            iconColor={props.dictationStatus === "recording" ? CRMColors.NEUTRAL_PAPER : CRMColors.NEUTRAL_INK}
                            icon={props.dictationStatus === "recording" ? "pause" : "microphone"}
                            loading={props.dictationStatus === "transcribing" || props.dictationStatus === "tidying"}
                            onClick={props.dictationStatus === "recording" ? props.onStopDictate : props.onClickDictate}
                            title={
                                props.dictationStatus === "recording"
                                    ? `Stop & transcribe (${formatElapsed(props.dictationElapsedSeconds)})`
                                    : props.dictationStatus === "transcribing"
                                        ? "Transcribing…"
                                        : props.dictationStatus === "tidying"
                                            ? "Tidying into an email…"
                                            : "Dictate email"
                            }
                        />

                        {/* ATTACH */}
                        {isEmailViewType("case", props.viewStatus) &&
                            <CRMButtonFlatColourIcon
                                backgroundColor={CRMColors.PRIMARY_12}
                                iconColor={CRMColors.NEUTRAL_2}
                                icon="attach"
                                onClick={props.onClickAttachment}
                                disabled={!props.isACaseSelected}
                                title="Select attachments"
                            />
                        }
                        
                        {/* SEND */}
                        <CRMButtonFlatColourIcon
                            backgroundColor={CRMColors.HIGHLIGHTS_POSITIVE_GREEN_0}
                            iconColor={CRMColors.NEUTRAL_12}
                            icon="send-email"
                            isRounded={true}
                            onClick={props.onSend}
                            disabled={!props.isSubmittable}
                            loading={props.isSendInProgress}
                            title={props.isSendInProgress ? "Reviewing email…" : "Send"}
                        />
                    </SpacingRow>
                </SpacingRow>
            </SpacingRow>
        </Background>
    );
};
